import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import Loading from '../components/Loading/Loading';
import Dialog from '../components/Dialog/Dialog';
import { DialogProps } from '../components/Dialog/Dialog';
interface AppContextType {
  loading: boolean;
  setLoading: (value: boolean) => void;
  showDialog: (
    title: string,
    message: string,
    type?: 'info' | 'error' | 'success' | 'confirm'
  ) => Promise<boolean>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState<{
    open: boolean;
    type: 'info' | 'error' | 'success' | 'confirm';
    title: string;
    message: string;
    resolve: ((value: boolean) => void) | null;
  }>({
    open: false,
    type: 'info',
    title: '',
    message: '',
    resolve: null,
  });

  const showDialog = useCallback(
    (title: string, message: string, type: 'info' | 'error' | 'success' | 'confirm' = 'info') => {
      return new Promise<boolean>((resolve) => {
        setDialog({
          open: true,
          type,
          title,
          message,
          resolve,
        });
      });
    },
    []
  );

  const handleDialogClose = (result: boolean) => {
    setDialog((prev) => {
      if (prev.resolve) prev.resolve(result);
      return { ...prev, open: false };
    });
  };

  return (
    <AppContext.Provider value={{ loading, setLoading, showDialog }}>
      {children}
      <Dialog
        open={dialog.open}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        onClose={handleDialogClose}
      />
      {loading && <Loading />}
    </AppContext.Provider>
  );
};

export function useAppContext() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppProvider');
  return ctx;
}
